/**
 * Live Plan Tracker
 * 
 * Takes a pre-race RacePlan (from Static Strategy Planner) and recalculates
 * the remaining strategy using real telemetry data from the live race.
 * 
 * Every lap, it:
 *   1. Records what actually happened (actual lap time, actual fuel used)
 *   2. Determines which stint we're currently in
 *   3. Re-simulates the remaining stints with real pace/fuel data
 *   4. Compares actual vs planned and shows deltas
 * 
 * This gives the driver a constantly-updating picture of whether the plan
 * is still on track, ahead, or behind — and what adjustments are needed.
 */

import type { RacePlan, PlanStint, StaticStrategyInput } from './staticStrategyPlanner';
import { StaticStrategyPlanner } from './staticStrategyPlanner';

// ─── Types ──────────────────────────────────────────────────────────────

export interface LiveLapData {
  lapNumber: number;
  lapTime: number;           // seconds (actual)
  fuelUsed: number;          // liters (actual)
  fuelRemaining: number;     // liters at end of lap
}

export interface TrackedStint {
  stintNumber: number;
  status: 'completed' | 'active' | 'planned';

  // Planned values (from original plan)
  plannedLaps: number;
  plannedFuelPerLap: number;
  plannedLapTime: number;
  plannedFuelAtStart: number;
  plannedPitAfter: boolean;
  plannedPitType: 'full' | 'splash' | null;
  plannedFuelAdded: number;
  plannedPitTime: number;

  // Actual values (filled as race progresses)
  actualLaps: number;
  actualAvgLapTime: number | null;
  actualAvgFuelPerLap: number | null;
  actualFuelAtStart: number | null;
  actualFuelAtEnd: number | null;

  // Delta
  lapTimeDelta: number | null;    // actual - planned (negative = faster)
  fuelDelta: number | null;       // actual - planned (negative = saving fuel)

  // Projected values for remaining laps in this stint (active stint only)
  projectedLapsRemaining: number | null;
  projectedFuelAtEnd: number | null;

  // Recalculated pit info (may differ from plan based on actual fuel state)
  recalcPitType: 'full' | 'splash' | null;
  recalcFuelToAdd: number | null;
  recalcPitTime: number | null;
}

export interface LivePlanStatus {
  // Original plan reference
  originalPlan: RacePlan;

  // Current race state
  currentLap: number;
  currentStint: number;
  currentFuel: number;
  elapsedTime: number;           // seconds
  remainingTime: number;         // seconds

  // Tracked stints (past + current + projected future)
  stints: TrackedStint[];

  // Projections from current point
  projectedTotalLaps: number;
  projectedRemainingStops: number;
  projectedFinishTime: number;   // total race time (seconds)

  // Deltas vs original plan
  lapsDelta: number;             // projected - planned (positive = more laps)
  timeDelta: number;             // projected - planned (negative = faster)
  fuelSaveDelta: number;         // avg actual fuel vs planned (negative = saving)

  // Overall status
  status: 'on_track' | 'ahead' | 'behind' | 'critical';
  statusMessage: string;

  // The re-planned strategy from current point forward
  updatedPlan: RacePlan | null;
}

// ─── Tracker Class ──────────────────────────────────────────────────────

export class LivePlanTracker {
  private originalPlan: RacePlan;
  private input: StaticStrategyInput;
  private planner: StaticStrategyPlanner;
  private lapHistory: LiveLapData[] = [];
  private pitLaps: Set<number> = new Set();  // laps where a pit stop occurred

  constructor(plan: RacePlan, input: StaticStrategyInput) {
    this.originalPlan = plan;
    this.input = input;
    this.planner = new StaticStrategyPlanner();
  }

  /**
   * Record a completed lap.
   */
  recordLap(lap: LiveLapData): void {
    // Avoid duplicates
    if (this.lapHistory.find(l => l.lapNumber === lap.lapNumber)) {
      // Update existing
      const idx = this.lapHistory.findIndex(l => l.lapNumber === lap.lapNumber);
      this.lapHistory[idx] = lap;
    } else {
      this.lapHistory.push(lap);
      this.lapHistory.sort((a, b) => a.lapNumber - b.lapNumber);
    }
  }

  /**
   * Record that a pit stop occurred after a given lap.
   */
  recordPitStop(afterLap: number): void {
    this.pitLaps.add(afterLap);
  }

  /**
   * Detect pit stops from fuel data: if fuel goes UP between laps, a pit happened.
   */
  private detectPitStops(): void {
    for (let i = 1; i < this.lapHistory.length; i++) {
      const prev = this.lapHistory[i - 1];
      const curr = this.lapHistory[i];
      // If fuel remaining went up, we pitted between these laps
      if (curr.fuelRemaining > prev.fuelRemaining + 0.5) {
        this.pitLaps.add(prev.lapNumber);
      }
    }
  }

  /**
   * Get the current stint number and which laps belong to each stint,
   * based on actual pit stop history.
   */
  private buildActualStints(): { stintNumber: number; laps: LiveLapData[] }[] {
    if (this.lapHistory.length === 0) return [];

    const stints: { stintNumber: number; laps: LiveLapData[] }[] = [];
    let currentStintLaps: LiveLapData[] = [];
    let stintNum = 1;

    for (const lap of this.lapHistory) {
      currentStintLaps.push(lap);

      if (this.pitLaps.has(lap.lapNumber)) {
        stints.push({ stintNumber: stintNum, laps: [...currentStintLaps] });
        currentStintLaps = [];
        stintNum++;
      }
    }

    // Current stint (not yet pitted)
    if (currentStintLaps.length > 0) {
      stints.push({ stintNumber: stintNum, laps: currentStintLaps });
    }

    return stints;
  }

  /**
   * Calculate rolling averages from recent laps (last N laps, excluding pit laps).
   * This gives a more accurate picture of current pace than overall average.
   */
  private getRecentAverages(windowSize: number = 5): { avgLapTime: number; avgFuelPerLap: number } {
    // Get non-pit laps (exclude in-lap and out-lap which have skewed times)
    const cleanLaps = this.lapHistory.filter((lap, i) => {
      // Exclude if this lap had a pit stop after it (in-lap)
      if (this.pitLaps.has(lap.lapNumber)) return false;
      // Exclude if previous lap had a pit stop (out-lap — may have warm-up penalty)
      if (i > 0 && this.pitLaps.has(this.lapHistory[i - 1].lapNumber)) return false;
      return lap.lapTime > 0 && lap.fuelUsed > 0;
    });

    if (cleanLaps.length === 0) {
      // Fallback to all valid laps
      const allValid = this.lapHistory.filter(l => l.lapTime > 0 && l.fuelUsed > 0);
      if (allValid.length === 0) {
        return { avgLapTime: this.input.avgLapTime, avgFuelPerLap: this.input.avgFuelPerLap };
      }
      const avgLT = allValid.reduce((s, l) => s + l.lapTime, 0) / allValid.length;
      const avgFPL = allValid.reduce((s, l) => s + l.fuelUsed, 0) / allValid.length;
      return { avgLapTime: avgLT, avgFuelPerLap: avgFPL };
    }

    const recent = cleanLaps.slice(-windowSize);
    const avgLapTime = recent.reduce((s, l) => s + l.lapTime, 0) / recent.length;
    const avgFuelPerLap = recent.reduce((s, l) => s + l.fuelUsed, 0) / recent.length;
    return { avgLapTime, avgFuelPerLap };
  }

  /**
   * Main update — call this every lap (or on demand) to get the full status.
   */
  update(currentFuel: number, raceTimeElapsed: number): LivePlanStatus {
    this.detectPitStops();

    const actualStints = this.buildActualStints();
    const { avgLapTime: realAvgLapTime, avgFuelPerLap: realAvgFuelPerLap } = this.getRecentAverages();
    const raceSeconds = this.input.raceDurationMinutes * 60;
    const remainingTime = Math.max(0, raceSeconds - raceTimeElapsed);

    const currentLap = this.lapHistory.length > 0
      ? this.lapHistory[this.lapHistory.length - 1].lapNumber
      : 0;
    const currentStintNum = actualStints.length > 0
      ? actualStints[actualStints.length - 1].stintNumber
      : 1;

    // ── Build tracked stints ──────────────────────────────────────────

    const trackedStints: TrackedStint[] = [];
    const plannedStints = this.originalPlan.stints;

    // Map actual stints to planned stints
    for (let i = 0; i < Math.max(plannedStints.length, actualStints.length); i++) {
      const planned = i < plannedStints.length ? plannedStints[i] : null;
      const actual = i < actualStints.length ? actualStints[i] : null;

      const isCompleted = actual !== null && i < actualStints.length - 1;
      const isActive = actual !== null && i === actualStints.length - 1;

      let actualAvgLT: number | null = null;
      let actualAvgFPL: number | null = null;
      let actualFuelStart: number | null = null;
      let actualFuelEnd: number | null = null;

      if (actual && actual.laps.length > 0) {
        const validLaps = actual.laps.filter(l => l.lapTime > 0);
        if (validLaps.length > 0) {
          actualAvgLT = validLaps.reduce((s, l) => s + l.lapTime, 0) / validLaps.length;
        }
        const validFuel = actual.laps.filter(l => l.fuelUsed > 0);
        if (validFuel.length > 0) {
          actualAvgFPL = validFuel.reduce((s, l) => s + l.fuelUsed, 0) / validFuel.length;
        }
        // Fuel at start of stint: the fuel remaining after the first lap + fuel used in first lap
        const firstLap = actual.laps[0];
        actualFuelStart = firstLap.fuelRemaining + firstLap.fuelUsed;
        // Fuel at end: last lap's remaining
        actualFuelEnd = actual.laps[actual.laps.length - 1].fuelRemaining;
      }

      // For active stint: project remaining laps
      let projectedLapsRemaining: number | null = null;
      let projectedFuelAtEnd: number | null = null;
      if (isActive && actualAvgFPL && actualAvgFPL > 0) {
        const safetyMargin = 0.5;
        const usableFuel = currentFuel - safetyMargin;
        projectedLapsRemaining = Math.max(0, Math.floor(usableFuel / actualAvgFPL));
        projectedFuelAtEnd = currentFuel - (projectedLapsRemaining * actualAvgFPL);
      }

      trackedStints.push({
        stintNumber: (planned?.stintNumber ?? (actual?.stintNumber ?? i + 1)),
        status: isCompleted ? 'completed' : isActive ? 'active' : 'planned',

        plannedLaps: planned?.laps ?? 0,
        plannedFuelPerLap: planned?.fuelPerLap ?? this.input.avgFuelPerLap,
        plannedLapTime: planned?.lapTime ?? this.input.avgLapTime,
        plannedFuelAtStart: planned?.fuelAtStart ?? this.input.fuelCapacity,
        plannedPitAfter: planned?.pitAfter ?? false,
        plannedPitType: planned?.pitType ?? null,
        plannedFuelAdded: planned?.fuelAdded ?? 0,
        plannedPitTime: planned?.pitTime ?? 0,

        actualLaps: actual?.laps.length ?? 0,
        actualAvgLapTime: actualAvgLT,
        actualAvgFuelPerLap: actualAvgFPL,
        actualFuelAtStart: actualFuelStart,
        actualFuelAtEnd: isActive ? currentFuel : actualFuelEnd,

        lapTimeDelta: actualAvgLT && planned ? actualAvgLT - planned.lapTime : null,
        fuelDelta: actualAvgFPL && planned ? actualAvgFPL - planned.fuelPerLap : null,

        projectedLapsRemaining,
        projectedFuelAtEnd,

        recalcPitType: null,
        recalcFuelToAdd: null,
        recalcPitTime: null,
      });
    }

    // ── Re-plan from current point forward ────────────────────────────

    // Use real pace data to re-simulate the remainder of the race
    // remainingLapsEstimate kept for future diagnostics
    void Math.ceil(remainingTime / realAvgLapTime);
    const remainingRaceMinutes = remainingTime / 60;

    let updatedPlan: RacePlan | null = null;
    if (remainingRaceMinutes > 0 && currentFuel > 0) {
      const replanInput: StaticStrategyInput = {
        ...this.input,
        avgFuelPerLap: realAvgFuelPerLap,
        avgLapTime: realAvgLapTime,
        raceDurationMinutes: remainingRaceMinutes,
      };

      const replanResult = this.planner.planRace(replanInput);
      if (replanResult.bestPlan.totalLaps > 0) {
        updatedPlan = replanResult.bestPlan;

        // Adjust the updated plan's stints to account for current fuel
        // The planner assumes start with full tank, but we actually start
        // with currentFuel. Re-simulate with the actual starting fuel.
        const customResult = this.replanFromCurrentState(
          currentFuel, remainingTime, realAvgLapTime, realAvgFuelPerLap
        );
        if (customResult) {
          updatedPlan = customResult;
        }
      }
    }

    // ── Calculate projections ─────────────────────────────────────────

    const completedLaps = currentLap;
    const projectedRemainingLaps = updatedPlan?.totalLaps ?? Math.floor(remainingTime / realAvgLapTime);
    const projectedTotalLaps = completedLaps + projectedRemainingLaps;
    const projectedRemainingStops = updatedPlan?.pitStops ?? 0;

    // actualElapsedDriving kept for future diagnostics
    void this.lapHistory.reduce((s, l) => s + l.lapTime, 0);
    const projectedFinishTime = raceTimeElapsed + (updatedPlan?.totalRaceTime ?? (projectedRemainingLaps * realAvgLapTime));

    // ── Deltas vs original plan ───────────────────────────────────────

    const lapsDelta = projectedTotalLaps - this.originalPlan.totalLaps;
    const timeDelta = projectedFinishTime - this.originalPlan.totalRaceTime;
    const fuelSaveDelta = realAvgFuelPerLap - this.originalPlan.adjustedFuelPerLap;

    // ── Status assessment ─────────────────────────────────────────────

    let status: LivePlanStatus['status'];
    let statusMessage: string;

    if (lapsDelta > 0) {
      status = 'ahead';
      statusMessage = `+${lapsDelta} laps ahead of plan. Pace: ${realAvgLapTime.toFixed(2)}s (plan: ${this.originalPlan.adjustedLapTime.toFixed(2)}s)`;
    } else if (lapsDelta < 0) {
      status = Math.abs(lapsDelta) >= 2 ? 'critical' : 'behind';
      statusMessage = `${lapsDelta} laps behind plan. Pace: ${realAvgLapTime.toFixed(2)}s (plan: ${this.originalPlan.adjustedLapTime.toFixed(2)}s)`;
    } else {
      // Same laps — check time
      const timeDiff = projectedFinishTime - this.originalPlan.totalRaceTime;
      if (Math.abs(timeDiff) < 30) {
        status = 'on_track';
        statusMessage = `On track. Pace: ${realAvgLapTime.toFixed(2)}s vs plan ${this.originalPlan.adjustedLapTime.toFixed(2)}s`;
      } else if (timeDiff < 0) {
        status = 'ahead';
        statusMessage = `On pace, ${Math.abs(timeDiff).toFixed(0)}s faster than plan`;
      } else {
        status = 'behind';
        statusMessage = `On pace, ${timeDiff.toFixed(0)}s slower than plan`;
      }
    }

    return {
      originalPlan: this.originalPlan,
      currentLap,
      currentStint: currentStintNum,
      currentFuel,
      elapsedTime: raceTimeElapsed,
      remainingTime,
      stints: trackedStints,
      projectedTotalLaps,
      projectedRemainingStops,
      projectedFinishTime,
      lapsDelta,
      timeDelta,
      fuelSaveDelta,
      status,
      statusMessage,
      updatedPlan,
    };
  }

  /**
   * Re-simulate from the current state (currentFuel, remaining time)
   * using the planner's simulation logic but with actual fuel level as start.
   */
  private replanFromCurrentState(
    currentFuel: number,
    remainingTime: number,
    realAvgLapTime: number,
    realAvgFuelPerLap: number,
  ): RacePlan | null {
    // Simple re-simulation: how many stints/pits do we need from here?
    const safetyMargin = 0.5;
    const raceSeconds = remainingTime;

    const hasCustomPit = this.input.pitInTime !== undefined
      && this.input.fuelingTime !== undefined
      && this.input.pitOutTime !== undefined;
    const refuelRate = hasCustomPit ? this.input.fuelingTime! / this.input.fuelCapacity : 0;

    const stints: PlanStint[] = [];
    let simTime = 0;
    let simLaps = 0;
    let fuel = currentFuel;
    let stintNum = 0;
    let stopsUsed = 0;

    // Estimate max stops needed
    const maxStintLaps = Math.floor((this.input.fuelCapacity - safetyMargin) / realAvgFuelPerLap);
    const roughLaps = Math.ceil(raceSeconds / realAvgLapTime) + 1;
    const maxStops = Math.max(0, Math.ceil(roughLaps / maxStintLaps));

    while (true) {
      stintNum++;
      const fuelAtStart = fuel;
      let lapsInStint = 0;
      let stintDriveTime = 0;

      while (true) {
        // White flag check
        const remaining = raceSeconds - simTime;
        if (remaining < realAvgLapTime && simLaps > 0) {
          if (fuel >= realAvgFuelPerLap) {
            simTime += realAvgLapTime;
            fuel -= realAvgFuelPerLap;
            lapsInStint++;
            simLaps++;
            stintDriveTime += realAvgLapTime;
          }
          stints.push({
            stintNumber: stintNum, laps: lapsInStint,
            fuelAtStart, fuelAtEnd: fuel,
            fuelUsed: fuelAtStart - fuel, stintTime: stintDriveTime,
            fuelPerLap: realAvgFuelPerLap, lapTime: realAvgLapTime,
            pitAfter: false, pitType: null, fuelAdded: 0, pitTime: 0,
          });
          return this.buildReplanResult(stints, simLaps, simTime, stopsUsed);
        }

        // Fuel check
        if (fuel - realAvgFuelPerLap < safetyMargin && lapsInStint > 0) {
          break;
        }

        simTime += realAvgLapTime;
        fuel -= realAvgFuelPerLap;
        lapsInStint++;
        simLaps++;
        stintDriveTime += realAvgLapTime;
      }

      // Need to pit
      if (stopsUsed < maxStops) {
        // Calculate if this is effectively the last stop
        const estPitTimeFull = hasCustomPit
          ? (this.input.pitInTime! + (this.input.fuelCapacity - fuel) * refuelRate + this.input.pitOutTime!)
          : 60;
        const remainingAfterPit = raceSeconds - simTime - estPitTimeFull;
        const lapsWithFullTank = Math.floor((this.input.fuelCapacity - safetyMargin) / realAvgFuelPerLap);
        const isLastStop = (remainingAfterPit / realAvgLapTime) <= lapsWithFullTank;

        let fuelToAdd: number;
        if (isLastStop) {
          // Iterative solver for last stop
          let estFuel = this.input.fuelCapacity - fuel;
          for (let iter = 0; iter < 3; iter++) {
            const iterPitTime = hasCustomPit
              ? (this.input.pitInTime! + estFuel * refuelRate + this.input.pitOutTime!)
              : 60;
            const remTime = raceSeconds - simTime - iterPitTime;
            const estRemLaps = Math.floor(remTime / realAvgLapTime) + 1;
            const fuelNeeded = estRemLaps * realAvgFuelPerLap + safetyMargin - fuel;
            estFuel = Math.max(0, Math.min(fuelNeeded, this.input.fuelCapacity - fuel));
          }
          fuelToAdd = estFuel;
        } else {
          fuelToAdd = this.input.fuelCapacity - fuel;
        }

        let pitTime: number;
        if (hasCustomPit) {
          pitTime = this.input.pitInTime! + fuelToAdd * refuelRate + this.input.pitOutTime!;
        } else {
          pitTime = 60;
        }

        const pitType: 'full' | 'splash' = fuelToAdd >= this.input.fuelCapacity * 0.5 ? 'full' : 'splash';

        stints.push({
          stintNumber: stintNum, laps: lapsInStint,
          fuelAtStart, fuelAtEnd: fuel,
          fuelUsed: fuelAtStart - fuel, stintTime: stintDriveTime,
          fuelPerLap: realAvgFuelPerLap, lapTime: realAvgLapTime,
          pitAfter: true, pitType, fuelAdded: fuelToAdd, pitTime,
        });

        simTime += pitTime;
        fuel += fuelToAdd;
        stopsUsed++;

        if (isLastStop) {
          stopsUsed = maxStops; // no more pitting
        }
      } else {
        // Out of stops — race over
        stints.push({
          stintNumber: stintNum, laps: lapsInStint,
          fuelAtStart, fuelAtEnd: fuel,
          fuelUsed: fuelAtStart - fuel, stintTime: stintDriveTime,
          fuelPerLap: realAvgFuelPerLap, lapTime: realAvgLapTime,
          pitAfter: false, pitType: null, fuelAdded: 0, pitTime: 0,
        });
        return this.buildReplanResult(stints, simLaps, simTime, stopsUsed);
      }

      if (stintNum > 50) break;
    }

    return null;
  }

  private buildReplanResult(
    stints: PlanStint[],
    totalLaps: number,
    totalTime: number,
    stops: number,
  ): RacePlan {
    const totalPitTime = stints.reduce((s, st) => s + st.pitTime, 0);
    const totalDrivingTime = stints.reduce((s, st) => s + st.stintTime, 0);
    const splashCount = stints.filter(s => s.pitType === 'splash').length;
    const fullCount = stints.filter(s => s.pitType === 'full').length;

    return {
      id: 'replan_live',
      name: `Live replan: ${fullCount > 0 ? fullCount + ' full' : ''}${splashCount > 0 ? (fullCount > 0 ? ' + ' : '') + splashCount + ' splash' : ''} ${stops === 0 ? 'no stops' : ''}`.trim(),
      description: `Re-planned from current state: ${totalLaps} laps remaining`,
      category: splashCount > 0 ? 'mixed_pit' : 'full_speed',
      totalLaps,
      totalRaceTime: totalTime,
      totalDrivingTime,
      totalPitTime,
      totalFuelSaveTimeCost: 0,
      fuelSavePercent: 0,
      adjustedFuelPerLap: stints.length > 0 ? stints[0].fuelPerLap : 0,
      adjustedLapTime: stints.length > 0 ? stints[0].lapTime : 0,
      pitStops: stints.filter(s => s.pitAfter).length,
      maxStintLaps: Math.max(...stints.map(s => s.laps), 0),
      pitPattern: 'all_full',
      stints,
      timeDeltaVsBest: 0,
      lapDeltaVsBest: 0,
      lapRangeLow: null,
      lapRangeHigh: null,
    };
  }

  /**
   * Reset all recorded data (e.g. on race restart).
   */
  reset(): void {
    this.lapHistory = [];
    this.pitLaps.clear();
  }

  /**
   * Get the original plan.
   */
  getOriginalPlan(): RacePlan {
    return this.originalPlan;
  }

  /**
   * Get recorded lap count.
   */
  getLapCount(): number {
    return this.lapHistory.length;
  }
}
